import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../auth/AuthContext";
import { CommunicationTimeline } from "./CommunicationTimeline";
import { SendMailModal } from "./SendMailModal";
import Ic from "../icons.jsx";

/**
 * "Komunikacija" card — the timeline plus its "Pošalji mejl" action, shared by
 * the lead detail and the customer detail page so both read identically.
 *
 * The whole card is internal-only, exactly like the backend timeline (a
 * CUST_MANAGER holds `companies:view` and can open their own company page, but
 * GET /api/communications answers 403 for them — so render nothing instead of
 * an error box). The send button is additionally gated on `mail:connect`;
 * internal readers without it still see the correspondence.
 */
export type CommunicationSectionProps = {
  leadId?: string | null;
  companyId?: string | null;
  /** Prefilled recipient — the lead's email, or empty to let the modal look up a company contact. */
  defaultTo?: string | null;
  defaultSubject?: string | null;
  /** Scopes the modal's offer picker to one customer company. */
  offerCompanyId?: string | null;
  readOnly?: boolean;
};

export function CommunicationSection({
  leadId,
  companyId,
  defaultTo,
  defaultSubject,
  offerCompanyId,
  readOnly = false,
}: CommunicationSectionProps) {
  const { t } = useTranslation();
  const { state, hasPermission } = useAuth();
  const [open, setOpen] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  if (state.status !== "authenticated" || !state.user.internal) return null;

  return (
    <section className="card">
      <header className="card-h">
        <div>
          <h3>{t("mail.section.title", "Correspondence")}</h3>
          <div className="sub">{t("mail.section.subtitle", "Emails sent from the portal and replies to them.")}</div>
        </div>
        {!readOnly && hasPermission("section:personal_mail:write") && hasPermission("mail:connect") && (
          <button type="button" className="btn primary sm" onClick={() => setOpen(true)}>
            <Ic.send /> {t("mail.section.send", "Send email")}
          </button>
        )}
      </header>
      <div className="card-b">
        <CommunicationTimeline leadId={leadId} companyId={companyId} reloadToken={reloadToken} />
      </div>

      <SendMailModal
        open={open && !readOnly && hasPermission("section:personal_mail:write")}
        onClose={() => setOpen(false)}
        onSent={() => setReloadToken((n) => n + 1)}
        leadId={leadId}
        customerCompanyId={companyId}
        defaultTo={defaultTo}
        defaultSubject={defaultSubject}
        offerCompanyId={offerCompanyId ?? companyId}
      />
    </section>
  );
}
